// src/components/layout/Sidebar.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FileText, CheckSquare, Users, Calendar,
  BarChart3, Settings, Activity, Building2, Database,
  Shield, ChevronLeft, ChevronRight, BookOpen, Eye, Lock, Archive,
} from 'lucide-react';
import { useSidebarStore } from '../../store/sidebarStore';
import { useAuthStore } from '../../store/authStore';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  roles?: string[];
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" />, href: '/dashboard' },
  {
    label: 'Laporan Masalah',
    icon: <FileText className="w-5 h-5" />,
    href: '/laporan',
    roles: ['kaprodi', 'kabag', 'ka_tu', 'admin', 'lpm', 'staff']
  },
  {
    label: 'Arsip Laporan',
    icon: <Archive className="w-5 h-5" />,
    href: '/arsip',
    roles: ['admin', 'lpm', 'rektor']
  },
  {
    label: 'Validasi', icon: <CheckSquare className="w-5 h-5" />, href: '/validasi',
    roles: ['dekan', 'kalem', 'warek', 'warek1', 'warek2', 'warek3', 'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4'],
    children: [
      { label: 'Level 1', icon: <Shield className="w-4 h-4" />, href: '/validasi/level1', roles: ['dekan', 'kalem'] },
      { label: 'Level 2', icon: <Shield className="w-4 h-4" />, href: '/validasi/level2', roles: ['warek', 'warek1', 'warek2', 'warek3', 'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4'] },
    ]
  },
  { label: 'RAPIM', icon: <Calendar className="w-5 h-5" />, href: '/rapim', roles: ['rektor', 'admin', 'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4', 'dekan', 'lpm'] },
  {
    label: 'Monitoring',
    icon: <Eye className="w-5 h-5" />,
    href: '/monitoring',
    roles: [
      'admin',
      'rektor',
      'dekan',
      'kalem',
      'lpm',
      'senat',
      'warek',
      'warek1',
      'warek2',
      'warek3',
      'wakil_rektor_1',
      'wakil_rektor_2',
      'wakil_rektor_3',
      'wakil_rektor_4',
    ],
  },
  { label: 'Laporan & Analitik', icon: <BarChart3 className="w-5 h-5" />, href: '/reports/builder' },
  {
    label: 'Admin', icon: <Settings className="w-5 h-5" />, href: '/admin',
    roles: ['admin'],
    children: [
      { label: 'Pengguna', icon: <Users className="w-4 h-4" />, href: '/admin/users' },
      { label: 'Unit Organisasi', icon: <Building2 className="w-4 h-4" />, href: '/admin/units' },
      { label: 'Master Data', icon: <Database className="w-4 h-4" />, href: '/admin/master' },
      { label: 'Standar SPMI', icon: <BookOpen className="w-4 h-4" />, href: '/admin/master?tab=standar' },
      { label: 'Role Scopes', icon: <Lock className="w-4 h-4" />, href: '/admin/role-scopes' },
      { label: 'Pengaturan', icon: <Settings className="w-4 h-4" />, href: '/admin/settings' },
      { label: 'Log Aktivitas', icon: <Activity className="w-4 h-4" />, href: '/admin/logs' },
    ]
  },
];

const Sidebar: React.FC = () => {
  const { isExpanded, toggle, isMobileOpen, setMobileOpen } = useSidebarStore();
  const { user } = useAuthStore();
  const { settings, isHydrated } = useSystemSettingsStore();
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = React.useState<string | null>(null);

  // State for image handling to prevent flicker/broken icons
  const [logoError, setLogoError] = React.useState(false);

  const userRole = user?.role?.name || localStorage.getItem('user_role') || '';

  React.useEffect(() => {
    if (user?.role?.name) {
      localStorage.setItem('user_role', user.role.name);
    }
  }, [user?.role?.name]);

  // Reset logo error when URL changes
  React.useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  const filteredItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(userRole) || userRole === 'admin';
  });

  const isActive = (href: string) => {
    const [path, query] = href.split('?');
    if (!location.pathname.startsWith(path)) return false;
    if (!query) return true;

    const currentParams = new URLSearchParams(location.search);
    const targetParams = new URLSearchParams(query);
    return Array.from(targetParams.entries()).every(([k, v]) => currentParams.get(k) === v);
  };
  const appName = settings.app_name || 'SIM-EVAL';
  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
  const footerText = settings.footer_text || '© 2026 UBP Karawang';

  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = logoUrl && !logoError;

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full bg-teal-dark text-white z-50
          transition-all duration-300 flex flex-col
          ${isExpanded ? 'w-64' : 'w-16'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <div className="flex items-center gap-2 overflow-hidden">
            <div
              className={`w-8 h-8 flex-shrink-0 flex items-center justify-center font-black text-sm overflow-hidden ${showLogo ? 'bg-transparent' : 'bg-teal-light'
                } ${showLogo ? 'rounded-md' : 'rounded-lg'}`}
            >
              {showLogo ? (
                <img
                  src={logoUrl!}
                  alt="Logo"
                  className="w-full h-full object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className="text-white">{brandInitials}</span>
              )}
            </div>
            {isExpanded && (
              <span className="font-bold text-sm tracking-tight truncate">{appName}</span>
            )}
          </div>
          <button onClick={toggle} className="p-1.5 rounded-lg hover:bg-white/10 hidden lg:block">
            {isExpanded ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <ul className="space-y-1">
            {filteredItems.map((item) => (
              <li key={item.href}>
                {item.children ? (
                  <div>
                    <button
                      onClick={() => setOpenSubmenu(openSubmenu === item.href ? null : item.href)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                        transition-colors duration-200
                        ${isActive(item.href) ? 'bg-teal-light/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}
                      `}
                    >
                      {item.icon}
                      {isExpanded && <span className="flex-1 text-left">{item.label}</span>}
                    </button>
                    {isExpanded && openSubmenu === item.href && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {item.children.filter(child => !child.roles || child.roles.includes(userRole) || userRole === 'admin').map((child) => (
                          <li key={child.href}>
                            <Link
                              to={child.href}
                              onClick={() => setMobileOpen(false)}
                              className={`
                                flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors
                                ${isActive(child.href) ? 'bg-teal-light/20 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'}
                              `}
                            >
                              {child.icon}
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                      transition-colors duration-200
                      ${isActive(item.href)
                        ? 'bg-teal-light/20 text-white border-l-2 border-teal-light'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                    `}
                    title={!isExpanded ? item.label : undefined}
                  >
                    {item.icon}
                    {isExpanded && <span>{item.label}</span>}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        {isExpanded && (
          <div className="p-4 border-t border-white/10">
            <p className="text-xs text-white/40 text-center">{footerText}</p>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;



